// WD-T2 (C): JHA due-today equipment surfacing — Tier-1 gear due for
// inspection with one-tap inspection entry (writes the standard
// AssetInspection via POST /api/assets/inspect), and the Tier-2 rigging kit
// as a SINGLE pre-use record: ONE AssetInspection covers the whole kit, never
// one record per device (zero-sticker — no serializing slings/shackles).
// Honest empty state: when nothing is due and there is no kit gear, this
// renders nothing instead of inventing work.
import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';

export interface DueEquipmentItem {
  assetId: number;
  name: string;
  type: string;
  serialNumber: string | null;
  cadence: string;
  nextInspectionDue: string | null;
  due: boolean;
  reason: 'daily-before-use' | 'past-due' | 'never-inspected' | null;
  tier: 1 | 2;
}

export interface KitItem {
  assetId: number;
  name: string;
  type: string;
}

export interface DueEquipmentData {
  items: DueEquipmentItem[];
  kit: {
    gearCount: number;
    checkedToday: boolean;
    checkedAt: string | null;
    kitAssetId: number | null;
    items: KitItem[];
  } | null;
}

const REASON_LABEL: Record<string, string> = {
  'daily-before-use': 'inspect before use today',
  'past-due': 'inspection past due',
  'never-inspected': 'never inspected — initial inspection required',
};

export function DueEquipmentChecklist({
  userId,
  crewLabel,
  jhaId,
  canRecordKitCheck = true,
}: {
  userId: number;
  crewLabel: string;
  jhaId?: number | null;
  /**
   * WD-T3 M4 (client): capability passed by the caller — true for foreman+
   * viewers, false for worker-tier viewers. The kit pre-use route is foreman+
   * by design (a worker pressing save gets 403), so when this is false the
   * save button and pass/fail radios are OMITTED: kit items render read-only
   * with an honest note that the foreman records the check. Callers pass the
   * viewer capability explicitly — never re-derive role logic here.
   */
  canRecordKitCheck?: boolean;
}) {
  const [data, setData] = useState<DueEquipmentData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [kitSaving, setKitSaving] = useState(false);
  // Kit item pass/fail marks — all of them land in the ONE kit record.
  const [kitMarks, setKitMarks] = useState<Record<number, 'PASS' | 'FAIL'>>({});
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get('/assets/due-today', {
        params: { userId, ...(jhaId ? { jhaId } : {}) }, // Sol MED-2: JHA context
      });
      setData(res.data.data);
      setLoaded(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the due-equipment list — retry, or record inspections from the asset tracker');
      setLoaded(true);
    }
  }, [userId, jhaId]);

  useEffect(() => {
    load();
  }, [load]);

  const oneTapPass = async (item: DueEquipmentItem) => {
    setBusyId(item.assetId);
    setMessage(null);
    try {
      await api.post('/assets/inspect', {
        assetId: item.assetId,
        status: 'PASS',
        inspectionKind: item.cadence === 'DAILY' ? 'FREQUENT' : 'PERIODIC',
        notes: `Pre-use inspection recorded from the JHA (crew: ${crewLabel})`,
        ...(jhaId ? { jhaId } : {}),
      });
      await load();
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Could not record the inspection — try again or use the asset tracker');
    } finally {
      setBusyId(null);
    }
  };

  const allKitMarked =
    !!data?.kit && data.kit.items.length > 0 && data.kit.items.every((i) => kitMarks[i.assetId]);

  const saveKitCheck = async () => {
    if (!data?.kit || !allKitMarked) return;
    setKitSaving(true);
    setMessage(null);
    try {
      const res = await api.post('/assets/kit-preuse', {
        crewLabel,
        items: data.kit.items.map((i) => ({ assetId: i.assetId, status: kitMarks[i.assetId] })),
        ...(jhaId ? { jhaId } : {}),
      });
      setMessage(res.data.message);
      setKitMarks({});
      await load();
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Could not record the kit check — try again or use the asset tracker');
    } finally {
      setKitSaving(false);
    }
  };

  if (!loaded) return null;
  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mt-2" data-testid="due-equipment-error">
        {error}
      </div>
    );
  }

  const items = data?.items ?? [];
  const kit = data?.kit ?? null;
  if (items.length === 0 && (!kit || kit.gearCount === 0)) return null;

  return (
    <div className="bg-white border border-indigo-200 rounded-lg p-3 mt-2" data-testid="due-equipment-checklist">
      <p className="text-sm font-semibold text-indigo-800 mb-1">Equipment inspections due today</p>
      <p className="text-xs text-gray-500 mb-2">
        Tracked gear is inspected individually. The rigging kit is ONE pre-use check — one record covers the kit.
      </p>
      {items.length > 0 && (
        <ul className="space-y-1 mb-2">
          {items.map((item) => (
            <li key={item.assetId} className="flex items-center justify-between text-sm gap-2">
              <span>
                <span className="font-medium text-gray-900">{item.name}</span>
                {item.serialNumber && <span className="text-gray-500"> · SN {item.serialNumber}</span>}
                <span className="text-gray-500"> · {REASON_LABEL[item.reason ?? ''] ?? 'inspection due'}</span>
              </span>
              <button
                type="button"
                data-testid={`due-equipment-pass-${item.assetId}`}
                disabled={busyId === item.assetId}
                onClick={() => oneTapPass(item)}
                className="px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              >
                {busyId === item.assetId ? 'Recording…' : 'Mark inspected ✓'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {kit && kit.gearCount > 0 && (
        <div className="border-t pt-2" data-testid="kit-preuse-row">
          <p className="text-sm">
            <span className="font-medium text-gray-900">Rigging kit pre-use check</span>
            <span className="text-gray-500"> · {kit.gearCount} kit item{kit.gearCount === 1 ? '' : 's'}</span>
          </p>
          {kit.checkedToday ? (
            <p className="text-xs text-green-700 mt-1">Kit checked today — one record covers the whole kit.</p>
          ) : !canRecordKitCheck ? (
            // WD-T3 M4 (client): worker viewer — the kit pre-use route is
            // foreman+ by design, so the kit renders READ-ONLY (names only, no
            // pass/fail marks, no save button) with an honest note instead of
            // a button that would 403.
            <>
              <ul className="text-xs text-gray-700 mt-1 list-disc list-inside" data-testid="kit-preuse-readonly">
                {kit.items.map((ki) => (
                  <li key={ki.assetId}>{ki.name}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-1" data-testid="kit-preuse-worker-note">
                Your foreman records the kit pre-use check — one record covers the whole kit.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 mt-1 mb-1">
                Mark each kit item pass/fail, then save — ONE record covers the whole kit.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {kit.items.map((ki) => (
                  <label key={ki.assetId} className="text-xs flex items-center gap-1">
                    <input
                      type="radio"
                      name={`kit-pass-${ki.assetId}`}
                      checked={kitMarks[ki.assetId] === 'PASS'}
                      onChange={() => setKitMarks((m) => ({ ...m, [ki.assetId]: 'PASS' }))}
                    />
                    <span className="font-medium">{ki.name}</span>
                    <span className="text-green-700">Pass</span>
                    <input
                      type="radio"
                      name={`kit-fail-${ki.assetId}`}
                      checked={kitMarks[ki.assetId] === 'FAIL'}
                      onChange={() => setKitMarks((m) => ({ ...m, [ki.assetId]: 'FAIL' }))}
                    />
                    <span className="text-red-700">Fail</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                data-testid="kit-preuse-save"
                disabled={!allKitMarked || kitSaving}
                onClick={saveKitCheck}
                className="mt-2 px-3 py-1 text-xs font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {kitSaving ? 'Saving…' : 'Record kit pre-use check'}
              </button>
            </>
          )}
        </div>
      )}
      {message && (
        <p className="text-xs text-gray-700 mt-2" role="status" data-testid="due-equipment-message">
          {message}
        </p>
      )}
    </div>
  );
}

export default DueEquipmentChecklist;